import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import type { FormContext } from '@/types/assistant';

// Module-level store - accessible across the React tree regardless of nesting
let currentFormContext: FormContext | null = null;
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return currentFormContext;
}

function setFormContext(ctx: FormContext | null) {
  currentFormContext = ctx;
  listeners.forEach((l) => l());
}

/**
 * Provider that registers form context when mounted.
 * Used in form pages (UnitFormPage, PropertyFormPage) so the
 * assistant knows which form the user is currently editing.
 */
export function AssistantFormProvider({
  children,
  formType,
  formSection,
  entityId,
  entityName,
}: { children: ReactNode } & FormContext) {
  useEffect(() => {
    setFormContext({ formType, formSection, entityId, entityName });
    return () => setFormContext(null);
  }, [formType, formSection, entityId, entityName]);

  return <>{children}</>;
}

/**
 * Hook to read the current form context from anywhere in the tree.
 * Works in DashboardLayout even though the provider is inside Outlet.
 */
export function useAssistantFormContext(): FormContext | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
